import langEE from "./langEE";
import langEN from "./langEN";
import langRU from "./langRU";
import * as echarts from "echarts/core";
import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { HealthPlanActivities, HealthPlanData, HealthPlanGoals } from "src/app/models/health-plan.model";
import { combineLatest, Observable, ReplaySubject } from "rxjs";
import { FetchClassificatorsService } from "src/app/services/fetch-classificators.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { GeneralRequestService } from "src/app/services/general-request.service";
import * as uuid from "uuid";
import { debounceTime, distinctUntilChanged, filter, takeUntil } from "rxjs/operators";
import { NgxMeasurementsService } from "../../../services/ngx-measurements.service";
import {
  ChartService,
  ChartServiceData,
  EgpApplication,
  EgpChartColor,
  EgpChartType,
  getDayString,
  getWeekStartString,
  ObservationChartType,
  Series,
  sortItems,
} from "../../../services/chart.service";
import { ModalService } from "../../../services/modal.service";
import { UserInfoService } from "../../../services/user-info.service";
import { DeviceDetectService } from "../../../services/device-detect.service";
import { NgxHealthPlanService } from "../../../features/health-plan/ngx-health-plan.service";
import { NgDebounce } from "../../../decorators/debounce.decorator";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { NgxEchartsDirective } from "ngx-echarts";
import { NgClass, NgStyle } from "@angular/common";
import { IconService } from "../../../services/icon.service";
import { addWeeks, endOfISOWeek, format, getISOWeek, getYear, isAfter, isValid, startOfISOWeek } from "date-fns";

echarts.registerLocale("EE", langEE);

/**
 * To edit the chart follow documentation at: https://echarts.apache.org/en/option.html#title
 * or the ng wrapper docs https://xieziyu.github.io/ngx-echarts/api-doc/index.html
 * It is possible to build a theme: https://echarts.apache.org/en/theme-builder.html
 * @locale options are EE, EN, RU (ee is custom)
 * To build chart when healthPlan related supply @healthPlan and @goal or @activity
 * Copied from new_doctor and adapted
 */


@Component({
    selector: "app-combo-echart",
    templateUrl: "./combo-echart.component.html",
    styleUrls: ["./combo-echart.component.css"],
    imports: [NgxEchartsDirective, NgClass, NgStyle, MatProgressSpinner, TranslateModule]
})
export class ComboEchartComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, AfterContentChecked {
  @Input() healthPlan?: HealthPlanData;
  @Input() goal?: HealthPlanGoals;
  @Input() activity?: HealthPlanActivities;
  @Input() useHealthPlanMarkLines: boolean = false;
  @Input() fullScreen: boolean = false;
  @Input() seriesMinMax: { min: number; max: number; name: string };
  @Input() currentZoom: { end: number; start: number } = { end: 100, start: 0 };
  @Input() destroyChart: boolean;
  @Output() closeModal: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() myId: string;

  chartOption: any;
  minHeight: number = 200;
  initOpts = { locale: "EE", renderer: "canvas" };
  thisChart: HTMLElement;
  @Input() chartType: EgpChartType;
  isLoading: boolean = false;
  noData: boolean = false;
  @Input() isStacked: boolean = true;
  @Input() isShowLines: boolean = true;
  @Input() isWeekView: boolean = true;
  @Input() isDayView: boolean = false;
  @Input() isShowBars: boolean = true;
  @Input() isSingleBars: boolean = false;
  @Input() getAllActivities: boolean = true;
  @Input() zoomFactor: number = 1;
  @Input() observationChartType: ObservationChartType;
  @Output() noDataEvent = new EventEmitter<void>();
  isMobile: boolean;
  private application: string;
  private dataChanges$: Observable<any>;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private echartsInstance: any;
  private personId: number;
  private data: Series[];
  private skipWeeks: boolean = false;
  private serviceData: ChartServiceData;

  constructor(private chartService: ChartService,
              private classificatorsService: FetchClassificatorsService,
              private ngxMeasurementsService: NgxMeasurementsService,
              private translate: TranslateService,
              private spinner: NgxSpinnerService,
              private generalRequestService: GeneralRequestService,
              private modalService: ModalService,
              private ngxHealthPlanService: NgxHealthPlanService,
              private userService: UserInfoService,
              private deviceDetectService: DeviceDetectService,
              private iconService: IconService,
  ) {
  }

  close(value: true) {
    this.closeModal.emit(value);
  }

  dataExists() {
    return !!this.data;
  }

  @HostListener("window:resize", ["$event"])
  @NgDebounce(100)
  onResize() {
    this.resizeChart();
  }

  onChartInit() {
  }

  resizeChart() {
    if (this.echartsInstance) {
      this.setDataZoom();
      this.echartsInstance.setOption(this.chartOption);
    }
  }

  ngAfterContentChecked() {
    this.generalRequestService.loadingDataStatus.pipe(takeUntil(this.destroyed$))
      .subscribe((status) => {
        if (Object.keys(status).length === 0) return;

        if (!this.healthPlan) {
          status.isHealthPlanDataLoading = false;
        }

        if (Object.values(status).some(value => value === true)) {
          this.isLoading = true;
          this.noData = false;
        } else {
          this.isLoading = false;
        }
      });
  }

  ngAfterViewInit() {
    this.thisChart = document.getElementById(this.myId);

    this.dataChanges$ = combineLatest([
      this.ngxMeasurementsService.observationsArrayChanges,
      this.ngxHealthPlanService.activityReportChanges,
      this.ngxHealthPlanService.activitiesChanges,
      this.ngxHealthPlanService.targetsChanges,
    ]);

    let lastObs: any[] = [];
    let lastReports: any[] = [];
    this.dataChanges$.pipe(
      distinctUntilChanged(),
      debounceTime(500),
      takeUntil(this.destroyed$),
    )
      .subscribe(([latestObs, latestReports]) => {

        let newObs = getNewItems(lastObs, latestObs.filter(obs => obs !== null || true)).flat();
        let newReports = getNewItems(lastReports, latestReports.filter(obs => obs !== null || true)).flat();
        if (newObs.some(item => item.personId === this.personId) || newReports.some(item => item.personId === this.personId)) {
          lastObs = [...latestObs.map(item => JSON.parse(JSON.stringify(item)))];
          lastReports = [...latestReports.map(item => JSON.parse(JSON.stringify(item)))];
          this.runUpdate();
        }
      });

    const objectsEqual = (o1, o2) =>
      typeof o1 === "object" && Object.keys(o1).length > 0 && o2 === "object"
        ? Object.keys(o1).length === Object.keys(o2).length &&
        Object.keys(o1).every(p => objectsEqual(o1[p], o2[p])) &&
        JSON.stringify(o1) === JSON.stringify(o2)
        : o1 === o2;

    const getNewItems = (prev, curr) => (
      [curr.filter(o1 => !prev.some(o2 => objectsEqual(o1, o2)))]
      // removed: prev.filter(o1 => !curr.some(o2 => objectsEqual(o1, o2)))
    );
  }

  runUpdate() {
    this.getChartType();
    this.getData();
  }

  ngOnDestroy() {
    if (this.echartsInstance) {
      this.echartsInstance.dispose();
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  setLocale(lang: string) {
    switch (lang) {
      case "EE":
        echarts.registerLocale("EE", langEE);
        break;
      case "EN":
        echarts.registerLocale("EN", langEN);
        break;
      case "RU":
        echarts.registerLocale("RU", langRU);
        break;
    }
  }

  ngOnInit(): void {
    this.isMobile = this.deviceDetectService.isMobile;
    const locale = this.getLocaleLang();
    this.setLocale(locale);
    this.getApplication();
    this.getPersonId();
    if (!this.chartOption) {
      this.setOptions();
    }

    if (!this.myId) {
      this.myId = uuid.v4();
    }

    this.chartService.forceExpandChart
      .pipe(
        filter(chartId => !!chartId && chartId === this.myId),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.refreshModalData(true, 1.5);
        this.modalService.showChartModal(this.serviceData);
      });

  }

  getChartType() {
    if (this.activity && !this.goal) {
      this.chartType = EgpChartType.BAR;
      this.minHeight = 200;
      return this.chartType;
    }
    if (this.goal) {
      if (this.isShowLines && this.isShowBars) {
        this.chartType = EgpChartType.COMBO;
      } else {
        this.chartType = EgpChartType.MULTI_LINE;
      }
      this.minHeight = 300;
      return this.chartType;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (changes.hasOwnProperty("destroyChart")) {
        if (!changes["destroyChart"].currentValue === true && this.echartsInstance) {
          this.echartsInstance.dispose();
        }
      } else if (changes.hasOwnProperty("goal")) {
        if (!changes["goal"].previousValue) {
        } else {
          if (changes["goal"].previousValue.id == changes["goal"].currentValue.id) {
          } else {
            this.getData();
          }
        }
      }
    }
  }

  getData() {
    this.setInstance();
    this.getPersonId();

    this.refreshModalData();
    this.data = this.chartService.generateChartData(this.serviceData);

    if (this.data.length === 0 || this.data[0] === undefined || this.data[0].data.length === 0) {
      this.noData = true;
      this.noDataEvent.emit();
      return;
    }
    this.noData = false;
    this.buildChart(this.data);
    return this.data;
  }

  scrollRight() {
    const newStart = this.currentZoom.start + (100 - this.currentZoom.end);
    this.echartsInstance.dispatchAction({
      type: "dataZoom",
      start: newStart,
      end: 100,
    });
  }

  refreshModalData(fullscreen?: boolean, zoomFactor?: number) {
    this.serviceData = {
      personId: this.personId,
      goal: this.goal,
      activity: this.activity,
      isStacked: this.isStacked,
      isShowBars: this.isShowBars,
      isShowLines: this.isShowLines,
      isSingleBars: this.isSingleBars,
      getAllActivities: this.getAllActivities,
      isDayView: this.isDayView,
      isWeekView: this.isWeekView,
      fullScreen: fullscreen ? fullscreen : this.fullScreen,
      currentZoom: this.currentZoom,
      seriesMinMax: this.seriesMinMax,
      healthPlan: this.healthPlan,
      zoomFactor: zoomFactor ? zoomFactor : this.zoomFactor,
      observationChartType: this.observationChartType,
      chartType: this.chartType,
    };
  }

  /**
   * Reordering the build process can cause inconsistency.
   */

  buildChart(data: Series[]) {
    if (this.echartsInstance) {
      this.echartsInstance.clear();
      this.setOptions();

      // set the chart colours for the height and weight diary chart
      if (this.serviceData.observationChartType === ObservationChartType.HEIGHT_WEIGHT) {
        this.chartOption.color = [
          EgpChartColor.GREEN,
          EgpChartColor.YELLOW,
          EgpChartColor.BLUE,
          EgpChartColor.MAIN,
          EgpChartColor.MAIN,
          EgpChartColor.MAIN,
          EgpChartColor.MAIN,
          EgpChartColor.MAIN,
          EgpChartColor.MAIN,
          EgpChartColor.MAIN,
        ];
      }
    }
    this.seriesMinMax = this.getSeriesMinMax(data);
    let index = 0;
    this.setCustomTooltip();
    for (let set of data) {
      this.setDots(set, index);
      this.setLegendNames(set);
      this.setYAxis(set, index, this.seriesMinMax);
      this.setHealthPlanStartEndMarker(set, this.seriesMinMax, index);
      this.setMarkArea(set, index);
      this.setVisualMap(set, index, this.seriesMinMax);
      this.setEmphasis(set);
      this.setSelectedSeries(set);
      this.setBarStyles(set, index);
      this.formatTooltipValue(set);
      this.setIcons(set);
      this.chartOption.series.push(set);
      index++;
    }
    this.setXAxis(data);
    this.setToolbox();
    this.setTitle(data);
    this.dispatchEvents();
    this.setDataZoom();
    this.setPadding();
    setTimeout(() => {
      if (this.echartsInstance) {
        this.echartsInstance.setOption(this.chartOption);
      }
    });
    this.isLoading = false;
    return this.chartOption;
  }


  setIcons(set: Series) {
    if (!set.activityTypeId) return;

    const mood = "assets/scalableVectorGraphics/chartIcons/small-mood.svg";
    const physical_low = "assets/scalableVectorGraphics/chartIcons/small-low-physical.svg";
    const measurement = "assets/scalableVectorGraphics/chartIcons/small-measurements.svg";
    const habit = "assets/scalableVectorGraphics/chartIcons/habits.svg";
    const sleep = "assets/scalableVectorGraphics/chartIcons/small-sleep.svg";
    const physical_high = "assets/scalableVectorGraphics/chartIcons/small-high-physical.svg";
    const medication = "assets/scalableVectorGraphics/chartIcons/small-medicines.svg";
    const nutrition = "assets/scalableVectorGraphics/chartIcons/small-nutrition.svg";

    let icon;
    switch (set.activityTypeId) {
      case (this.chartService.activityTypes.MOOD):
        icon = mood;
        break;
      case (this.chartService.activityTypes.PHYSICAL_LOW):
        icon = physical_low;
        break;
      case (this.chartService.activityTypes.MEASUREMENT):
        icon = measurement;
        break;
      case (this.chartService.activityTypes.HABIT):
        icon = habit;
        break;
      case (this.chartService.activityTypes.SLEEP):
        icon = sleep;
        break;
      case (this.chartService.activityTypes.PHYSICAL_HIGH):
        icon = physical_high;
        break;
      case (this.chartService.activityTypes.MEDICATION):
        icon = medication;
        break;
      case (this.chartService.activityTypes.NUTRITION):
        icon = nutrition;
        break;
    }

    let label = {
      show: true,
      position: "insideBottom",
      distance: 0,
      formatter: () => {
        return "{icon|}";
      },
      rich: {
        icon: {
          align: "right",
          backgroundColor: {
            image: icon,
          },
          width: (15 * this.zoomFactor),
          height: (15 * this.zoomFactor),
          color: "#eee",
        },
      },
    };

    objAssign(set, { label: label });
  }

  getPersonId() {
    if (this.healthPlan) {
      this.personId = this.healthPlan.personId;
    } else {
      // get person id from the session
      this.personId = this.userService.getSessionContext().principal.id;
    }
  }

  setInstance() {
    if (!this.thisChart) return;
    this.echartsInstance = echarts.getInstanceByDom(this.thisChart);
    if (!this.echartsInstance) return;
    // this.echartsInstance.resize();
  }

  /**
   * Limit maxZoom with maxSpan (%) so data would not stack when zooming too much.
   */
  setDataZoom() {
    // if (!this.echartsInstance) return;
    Object.assign(this.chartOption, { dataZoom: [] });
    let dataZoom = {
      type: "inside",
      filterMode: "none",
      realtime: true,
      start: 0,
      end: 100,
    };
    let slider = {
      type: "slider",
      brushSelect: false,
      handleIcon: "path://M30.9,53.2C16.8,53.2,5.3,41.7,5.3,27.6S16.8,2,30.9,2C45,2,56.4,13.5,56.4,27.6S45,53.2,30.9,53.2z M30.9,3.5C17.6,3.5,6.8,14.4,6.8,27.6c0,13.3,10.8,24.1,24.101,24.1C44.2,51.7,55,40.9,55,27.6C54.9,14.4,44.1,3.5,30.9,3.5z M36.9,35.8c0,0.601-0.4,1-0.9,1h-1.3c-0.5,0-0.9-0.399-0.9-1V19.5c0-0.6,0.4-1,0.9-1H36c0.5,0,0.9,0.4,0.9,1V35.8z M27.8,35.8 c0,0.601-0.4,1-0.9,1h-1.3c-0.5,0-0.9-0.399-0.9-1V19.5c0-0.6,0.4-1,0.9-1H27c0.5,0,0.9,0.4,0.9,1L27.8,35.8L27.8,35.8z",
      height: 15,
      bottom: 5,
      showDetail: false,
      end: null,
      start: null,
    };

    if (this.fullScreen) {
      objAssign(slider, { bottom: 10 });
    }

    if (this.chartOption.series.some(set => set.type === "bar")) {
      if (!this.thisChart) return;
      let bar = this.chartOption.series.find(set => set.type === "bar");
      let xAxisWidth = this.thisChart.clientWidth;
      let numberOfCols = this.chartOption.series.length;
      let numberOfSets = this.chartOption.xAxis[0].data.length;
      let barWidth = bar.barWidth;
      let maxSpan = 100;
      Object.assign(dataZoom, { maxSpan: maxSpan });
      if (bar && checkProp(bar, "barCategoryGap")) {
        let boundaryGap = parseInt(bar.barCategoryGap.replace("%", ""));
        maxSpan = (xAxisWidth * 100) / (numberOfSets * numberOfCols * barWidth) * ((100 - boundaryGap) / 100);
      }
      dataZoom.start = (100 - maxSpan);
    }

    if (this.fullScreen) {
      slider.height = (slider.height * this.zoomFactor);
    }

    if (this.currentZoom) {
      slider.end = this.currentZoom.end;
      slider.start = this.currentZoom.start;
    }

    this.chartOption.dataZoom.push(dataZoom);
    this.chartOption.dataZoom.push(slider);
    return dataZoom;
  }

  /**
   * Some icons from google icons https://fonts.google.com/icons
   */
  setToolbox() {

    let barSets = this.data.filter(set => set.type === "bar");

    this.chartOption.toolbox = {
      itemSize: (20 * this.zoomFactor),
      iconStyle: {
        color: EgpChartColor.MAIN,
      },
      feature: {
        myToolFullscreen: {
          show: !this.fullScreen,
          title: this.translate.instant("healthPlan.fullscreen"),
          icon: this.iconService.getIcon("expand"),
          onclick: () => {
            setTimeout(() => {
              this.refreshModalData(true, 1.5);
              this.modalService.showChartModal(this.serviceData);
            }, 100);
          },
        },
        myToolDayLineView: {
          show: this.isWeekView && [EgpChartType.LINE, EgpChartType.MULTI_LINE, EgpChartType.COMBO].includes(this.chartType),
          title: this.translate.instant("healthPlan.dayView"),
          icon: this.iconService.getIcon("calendar_day_black"),
          onclick: () => {
            this.isWeekView = false;
            this.isDayView = true;
            if (this.isDayView) {
              this.isShowLines = true;
              this.isShowBars = false;
            }
            if (this.echartsInstance) {
              this.echartsInstance.dispose();
              this.echartsInstance = null;
            }

            const dom = document.getElementById(this.myId);
            if (dom) {
              this.echartsInstance = echarts.init(dom);
            }

            this.runUpdate();

            setTimeout(() => {
              if (this.echartsInstance) {
                this.echartsInstance.resize();
              }
            }, 300);
          },
        },
        myToolWeekLineView: {
          show: this.isDayView &&
            [EgpChartType.LINE, EgpChartType.MULTI_LINE, EgpChartType.COMBO].includes(this.chartType),
          title: this.translate.instant("healthPlan.weekView"),
          icon: this.iconService.getIcon("calendar_week_black"),
          onclick: () => {
            this.isWeekView = true;
            this.isDayView = false;
            if (this.isWeekView) {
              this.isShowBars = true;
              this.isStacked = true;
              this.isSingleBars = false;
            }
            if (this.echartsInstance) {
              this.echartsInstance.dispose();
              this.echartsInstance = null;
            }

            const dom = document.getElementById(this.myId);
            if (dom) {
              this.echartsInstance = echarts.init(dom);
            }

            this.runUpdate();

            setTimeout(() => {
              if (this.echartsInstance) {
                this.echartsInstance.resize();
              }
            }, 300);
          },
        },
        myToolShowHideLines: {
          show: this.isWeekView && [
            EgpChartType.COMBO,
            EgpChartType.MULTI_LINE,
          ].includes(this.chartType) && barSets.length > 0,
          title: this.translate.instant("healthPlan.lines"),
          icon: this.iconService.getIcon("show_chart_black"),
          iconStyle: {
            opacity: this.isShowLines ? 1 : 0.3,
          },
          onclick: () => {
            this.isShowLines = !this.isShowLines;
            this.runUpdate();
          },
        },
        myToolIndividualBars: {
          show: this.chartType === EgpChartType.BAR && this.isStacked && this.activity && barSets.some(set => [
            this.chartService.activityTypes.PHYSICAL_LOW,
            this.chartService.activityTypes.PHYSICAL_HIGH,
          ].includes(set.activityTypeId)) && barSets.length > 1,
          title: this.translate.instant("healthPlan.normalBar"),
          icon: this.iconService.getIcon("bar_chart_black"),
          onclick: () => {
            this.isSingleBars = !this.isSingleBars;
            if (this.isSingleBars) {
              this.isShowBars = true;
              this.isStacked = false;
            }
            this.runUpdate();
          },
        },
        myToolStackedBars: {
          show: this.chartType === EgpChartType.BAR && this.isSingleBars && this.activity && barSets.some(set => [
            this.chartService.activityTypes.PHYSICAL_LOW,
            this.chartService.activityTypes.PHYSICAL_HIGH,
          ].includes(set.activityTypeId)) && barSets.length > 1,
          title: this.translate.instant("healthPlan.stackedBar"),
          icon: this.iconService.getIcon("stacked_bar_chart_black"),
          onclick: () => {
            this.isStacked = !this.isStacked;
            if (this.isStacked) {
              this.isShowBars = true;
              this.isSingleBars = false;
            }
            this.runUpdate();
          },
        },
        myToolUpdate: {
          show: true,
          title: this.translate.instant("healthPlan.updateChart"),
          icon: this.iconService.getIcon("refresh_black"),
          onclick: async () => {
            await this.ngxMeasurementsService.fetchAllObservations(this.personId);
            await this.ngxHealthPlanService.fetchHealthPlanActivityReport(this.personId);
            this.runUpdate();
          },
        },
        dataView: {
          show: this.fullScreen,
          readOnly: false,
          //optionToContent: (opt) => this.getTableContent(opt),
          icon: this.iconService.getIcon("table_view_black"),
        },
        saveAsImage: {
          show: true,
          type: "png",
          icon: this.iconService.getIcon("download_for_offline_black"),
        },
        myToolCloseModal: {
          show: !!this.fullScreen,
          title: this.translate.instant("healthPlan.close"),
          icon: this.iconService.getIcon("close_black"),
          onclick: () => {
            this.fullScreen = false;
            this.zoomFactor = 1;
            this.refreshModalData();
            this.close(true);
          },
        },
      },
    };
  }


  setCustomTooltip() {

    const tooltip = {
      confine: true,
      position: function(point) {
        return [point[0] + 10, point[1] + 10];
      },
      extraCssText: `min-width: 250px !important;!important;border-style: solid;z-index: 100;background-color: rgb(255, 255, 255);border-radius: 4px;color: rgb(102, 102, 102);`,
      formatter: (params) => {
        let weekString = " • " + this.translate.instant("healthPlan.week");
        let weekNr = getISOWeek(new Date(params[0].data[0]));
        let title: string;
        if ([EgpChartType.BAR, EgpChartType.COMBO].includes(this.chartType)) {
          const start = format(startOfISOWeek(new Date(params[0].name)), "dd.MM.yyyy");
          const end = format(endOfISOWeek(new Date(params[0].name)), "dd.MM.yyyy");
          title = `${start} - ${end}`;
        } else {
          const date = new Date(params[0].data[0]);
          title = `${format(date, "dd.MM.yyyy")} • ${format(date, "HH:mm")}`;
        }
        const titleStyle = `padding: 0 6px;height: 22px;`;
        const rowStyle = `display: flex;padding: 0 6px;width: 100%;justify-content: space-between;`;
        const circleStyle = `display:flex;margin-right:5px;border-radius:10px;width:14px;height:12px;`;

        let res = `<div style='${titleStyle}'><p>` + title + weekString + ` <strong>${weekNr}</strong> </p></div>`;
        for (let i = 0; i < params.length; i++) {
          let color;
          if (params[i].color.colorStops) {
            color = params[i].color.colorStops[0]["color"].split(",", 3) + ", 1)";
          } else {
            color = color = params[i].color;
          }

          let averageString = "";
          if (this.chartType === EgpChartType.COMBO && params[i].seriesType !== "bar") {
            averageString = this.translate.instant("healthPlan.weekAverage") + " ";
          }

          res += `<div style="display: flex;">
                  <span style="${circleStyle};background-color:${color};"></span>
                  <div style="${rowStyle}">
                  <span style=" padding-right: 6px;">${averageString}${params[i].seriesName}</span>
                  <span style="font-weight: 800">${params[i].data[1]} ${params[i].data[2]}</span>
                  </div>
                  </div>
                  `;
        }

        if (this.goal) {
          if (this.goal.typeId === this.chartService.goalTypes.WEIGHT) {
            const latestWeekHeight = this.ngxMeasurementsService.getLatestObservationByDateAndType(new Date(params[0].data[0]), this.chartService.obsTypes.height, this.personId);
            if (latestWeekHeight) {
              const heightString = this.translate.instant("healthPlan.weekLastHeight");
              const heightValue = latestWeekHeight.value + " " + this.chartService.getLabel(this.chartService.obsTypes.height);
              res += `<div style="display: flex;">
                  <span style="${circleStyle};background-color:#242e66;"></span>
                  <div style="${rowStyle}">
                  <span style=" padding-right: 6px;">${heightString}</span>
                  <span style="font-weight: 800">${heightValue}</span>
                  </div>
                  </div>
                  `;
            }
            const latestWeekWaist = this.ngxMeasurementsService.getLatestObservationByDateAndType(new Date(params[0].data[0]), this.chartService.obsTypes.waist, this.personId);
            if (latestWeekWaist) {
              const waistString = this.translate.instant("healthPlan.weekLastWaist");
              const waitsValue = latestWeekWaist.value + " " + this.chartService.getLabel(this.chartService.obsTypes.waist);
              res += `<div style="display: flex;">
                  <span style="${circleStyle};background-color:#242e66;"></span>
                  <div style="${rowStyle}">
                  <span style=" padding-right: 6px;">${waistString}</span>
                  <span style="font-weight: 800">${waitsValue}</span>
                  </div>
                  </div>
                  `;
            }
          }
        }


        return res;
      },
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
    };

    if ([EgpChartType.BAR, EgpChartType.COMBO].includes(this.chartType) || !this.serviceData.isShowLines) {
      tooltip.axisPointer.type = "shadow";
    } else {
      tooltip.axisPointer.type = "line";
      Object.assign(tooltip.axisPointer, { lineStyle: { width: 2, type: "solid", color: EgpChartColor.MAIN } });

    }
    this.chartOption.tooltip = tooltip;
  }

  /**
   * Format tooltip label value
   */

  formatTooltipValue(set: Series) {
    const label = this.chartService.getLabel(set.classificatorCode);
    Object.assign(set, { tooltip: {} });
    Object.assign(set.tooltip, { valueFormatter: (value) => value.toFixed(0) + " " + label });
    return set;
  }


  setXAxis(data: Series[]) {
    let xAxis = {
      // boundaryGap: ['1%', '1%'],
      minInterval: 0,
      alignWithLabel: true,
      type: "category",
      margin: 3,
      data: this.getXAxisDates(data),
      axisLabel: {
        // showMinLabel: true,
        // showMaxLabel: true,
        fontSize: (12 * this.zoomFactor),
        hideOverlap: true,
        formatter: null,
        rich: {
          yearStyle: { // Make yearly text more standing out
            color: "#000",
            fontWeight: "bold",
            fontSize: (12 * this.zoomFactor),
          },
          monthStyle: {
            color: "#999",
            fontSize: (12 * this.zoomFactor),
          },
        },
      },
      axisTick: {
        alignWithLabel: true,
      },
    };

    let firstDays = this.getFirstDatesOfYear(xAxis.data);
    if (this.isDayView) {
      xAxis.type = "time";
      xAxis.axisLabel.formatter = { // only works for time type
        year: "{monthStyle|{MMM}}\n{yearStyle|{yyyy}}",
        month: "{monthStyle|{MMM}}",
      };
    } else if (this.isWeekView) {
      xAxis.type = "category";
      xAxis.axisLabel.formatter = function(value) {
        const year = getYear(new Date(value));
        const week = getISOWeek(new Date(value));
        if (firstDays.includes(value)) {
          return week + "\n" + year;
        } else {
          return week;
        }
      };
    }

    this.chartOption.xAxis.push(xAxis);
  }

  getFirstDatesOfYear(data: any[]) {
    let temp = data.sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf())
      .reduce((acc, curr) => {
        const year = new Date(curr).getFullYear();
        if (!acc[year]) acc[year] = curr;
        return acc;
      }, {});
    return Object.values(temp);
  }


  setBarStyles(set: Series, index: number) {
    if (set.type != "bar") return;

    let target;
    if (set.activityTarget > 0) {
      target = set.activityTarget;
    }

    let setYAxis = this.chartOption.yAxis.find(axis => axis.id === set.name + index);
    if (setYAxis) {
      if (setYAxis.max && target) {
        if (setYAxis.max < target) {
          target = setYAxis.max;
        }
      }
    }


    if (!checkProp(set, "barWidth")) {
      if (this.fullScreen) {
        objAssign(set, { barWidth: (10 * this.zoomFactor) });
      } else {
        objAssign(set, { barWidth: 10 });
      }
    }
    if (!checkProp(set, "barCategoryGap")) {
      objAssign(set, { barCategoryGap: "30%" });
    }
    if (!checkProp(set, "itemStyle")) {
      let itemStyle = {
        borderRadius: [10, 10, 0, 0],
        color(params) {
          const colors = ["rgba(36, 46, 102,"];
          let color = colors[0];
          // let color = colors[params.componentIndex];
          let maxOpacity = 0.3;
          let minOpacity = 0.05;
          const barValue = params.data[1];

          if (target && barValue <= target) {
            maxOpacity = minOpacity + (barValue * (maxOpacity - minOpacity) / target);
          }

          let maxColor = `${color} ${maxOpacity})`;
          let minColor = `${color} ${minOpacity})`;
          return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: maxColor,
            }, {

              offset: 1,
              color: minColor,
            },
          ]);
        },
      };
      objAssign(set, { itemStyle: itemStyle });
    }
    return set;
  }


  setSelectedSeries(set: Series) {
    // hide waist and height lines for health plan charts
    if (!this.serviceData.observationChartType
      && [this.chartService.obsTypes.waist, this.chartService.obsTypes.height].includes(set.classificatorCode)) {
      objAssign(this.chartOption.legend.selected, { [set.name]: false });
      return this.chartOption.legend.selected;
    }
  }


  setEmphasis(set: Series) {
    if (!set.hasOwnProperty("emphasis")) {
      Object.assign(set, {
        emphasis: {
          focus: "series",
        },
      });
      return set;
    }
  }

  dispatchEvents() {
    if (!this.thisChart) return;
    // let instance = echarts.getInstanceByDom(this.thisChart);
    // if (!instance) return;

    // myChart.dispatchAction({
    //   type: 'downplay',
    //   seriesIndex: 0,
    //   dataIndex: currentIndex
    // });
    // currentIndex = (currentIndex + 1) % dataLen;
    // instance.dispatchAction({
    //   type: 'legendSelect',
    //   name: 'Ümbermõõt'
    // });
    // console.log('dispatched');
    // myChart.dispatchAction({
    //   type: 'showTip',
    //   seriesIndex: 0,
    //   dataIndex: currentIndex
    // });
  }

  /**
   * Creates gray underlay for goal range
   * Creates toggleable legend when show = true
   * When target is not range type, create markLine instead
   */

  setMarkArea(set: Series, index: number) {
    let mark = {
      itemStyle: {
        borderColor: EgpChartColor.DARK_GRAY,
        borderType: "dashed",
        color: "transparent",
        borderWidth: (1 * this.zoomFactor),
      },
      label: {
        show: true,
        fontSize: (8 * this.zoomFactor),
        color: EgpChartColor.DARK_GRAY,
        position: "inside",
      },
      emphasis: {
        label: {
          show: true,
          fontSize: (8 * this.zoomFactor),
          color: EgpChartColor.DARK_GRAY,
          position: "inside",
        },
        itemStyle: {
          borderColor: EgpChartColor.DARK_GRAY,
          borderType: "solid",
          color: "transparent",
          borderWidth: (1 * this.zoomFactor),
        },
      },
      data: [],
    };

    let markData = [
      { name: "", yAxis: 130 }, { yAxis: 170 },
    ];

    if (this.goal) {
      for (let target of this.goal.targets) {
        if (target.measureId == set.classificatorCode) {
          if (target.detailRangeMin && target.detailRangeMax) {
            let max = parseFloat(target.detailRangeMax);
            let min = parseFloat(target.detailRangeMin);
            markData[0].yAxis = max;
            markData[1].yAxis = min;
            markData[0].name = (set.name + " eesmärk " + min + " - " + max + " ").toUpperCase();
            mark.data.push(markData);
            set.markArea = mark;
          } else if (target.detailValue) {
            let value = parseFloat(target.detailValue);
            let text = (set.name + " eesmärk ").toUpperCase();
            this.setSingleMarkLine(set, text, value, this.getSetName(set));
          }
        }
      }
    }


    if (this.activity) {
      if (this.chartType === EgpChartType.COMBO) return;
      if (index === this.data.length - 1) {
        if (this.data[this.data.length - 1].type === "bar" && this.chartType === EgpChartType.BAR) {
          let value = set.activityTarget;
          let text = (" eesmärk ").toUpperCase();
          this.setSingleMarkLine(set, text, value, this.getSetName(set));
        }
      }
    }
  }


  /**
   * Paints lines red when goal is out of range
   * Creates toggleable legend when show = true
   */

  setVisualMap(set: Series, index: number, seriesMinMax: any) {
    if (![EgpChartType.COMBO, EgpChartType.LINE, EgpChartType.MULTI_LINE].includes(this.chartType)) return;
    if (!this.goal) return;
    if (!this.goal.targets) return;
    if (set.goalTypeId != this.goal.typeId) return;

    let map = {
      itemGap: 5,
      textGap: 5,
      top: 40,
      show: false,
      type: "piecewise",
      selectedMode: "multiple",
      right: "1%",
      dimension: 1,
      text: ["", "Low"],
      orient: "vertical",
      seriesIndex: index,
      itemSymbol: "emptyCircle",
      outOfRange: null,
      inRange: null,
      emphasis: {
        itemStyle: {
          borderWidth: (5 * this.zoomFactor),
        },
      },
      pieces: [],
    };

    let piece = { symbolSize: (6 * this.zoomFactor), min: 0, max: 0, label: "" };
    let piece2 = { symbolSize: (6 * this.zoomFactor), min: 0, max: 0, label: "" };

    for (let target of this.goal.targets) {
      if (target.measureId == set.classificatorCode) {
        if (target.detailRangeMin && target.detailRangeMax) { // range type

          let outOfRange = { symbol: "emptyCircle", symbolSize: (6 * this.zoomFactor) };
          let inRange = {
            color: "red",
            symbol: "emptyCircle",
            symbolSize: (6 * this.zoomFactor),
            itemStyle: { borderWidth: (2 * this.zoomFactor) },
          };

          piece.min = 0;
          piece.max = parseFloat(target.detailRangeMin) - 0.01;
          piece2.min = parseFloat(target.detailRangeMax) + 0.01;
          piece2.max = seriesMinMax.max > parseFloat(target.detailRangeMax) ? seriesMinMax.max + 0.01 : parseFloat(target.detailRangeMax) + 0.01;
          // map.text[1] = target.detailRangeMin + ' - ' + target.detailRangeMax;
          map.top = map.top + (index * 80);
          map.inRange = inRange;
          map.outOfRange = outOfRange;
          map.pieces.push(piece);
          map.pieces.push(piece2);

        } else if (target.detailValue) { // single target type

          let outOfRange = { symbol: "emptyCircle", symbolSize: (6 * this.zoomFactor) };
          let inRange = {
            color: "red",
            symbol: "emptyCircle",
            symbolSize: (6 * this.zoomFactor),
            itemStyle: { borderWidth: (7 * this.zoomFactor) },
          };

          piece.max = seriesMinMax.max > parseFloat(target.detailValue) ? seriesMinMax.max : parseFloat(target.detailValue);
          piece.min = parseFloat(target.detailValue);
          // map.text[1] = ' > ' + target.detailRangeMin;
          map.top = map.top + (index * 20);
          map.inRange = inRange;
          map.outOfRange = outOfRange;
          map.pieces.push(piece);

        }

        if (set.classificatorCode === this.chartService.obsTypes.PEF) {
          if (map.inRange && map.outOfRange) {
            const temp = map.inRange;
            map.inRange = map.outOfRange;
            map.outOfRange = temp;
          }
        }


        // if (map.pieces.length > 0) map.show = true; //TODO
        if (!this.fullScreen) {
          map.show = false;
        }
        this.chartOption.visualMap.push(map);
      }
    }

  }

  onLegendChange(event: any) {
    if (Object.values(event.selected).some(v => v === true)) {
      return event;
    }
  }


  setTitle(data: Series[]) {
    if (!this.fullScreen) return;
    let titleString = "";
    if (this.application === EgpApplication.DOCTOR && this.fullScreen) {
      // const patient = this.patientService.filteredPatients.getValue().find(patient => patient.id === this.personId);
      const patient = this.userService.getSessionContext().principal;
      if (patient) {
        titleString = ` - ${patient.firstName} ${patient.lastName} • ${patient.personalCode}`;
      }
    }
    let title = {
      text: "",
      subtext: "",
      padding: [
        10,  // up
        10, // right
        // 5,  // down
        10, // left
      ],
    };
    if (this.goal) {
      if (this.goal.description) {
        title.text = this.goal.description + titleString;
      }
    } else if (this.activity) {
      if (this.activity.description) {
        title.text = this.activity.description + titleString;
      }
    }
    this.chartOption.title = title;
  }

  setPadding() {
    let grid = {
      top: (40 * this.zoomFactor),
      left: 10,
      right: 20,
      bottom: 40,
      // containLabel: true
    };
    if (this.chartOption.dataZoom) {
      let slider = this.chartOption.dataZoom.filter(zoom => zoom.type === "slider");
      if (slider > 0) {
        if (!slider[0].height) return;
        grid.bottom += (parseInt(slider[0].height) * this.zoomFactor);
      }
    }

    if (this.chartOption.yAxis.some(axis => axis.show === true)) {
      grid.left = 50;
    }

    if (this.chartOption.title.text.length > 0) {
      grid.top += 20;
    }

    if (this.chartOption.title.subtext.length > 0) {
      grid.top += 20;
    }


    this.chartOption.grid = grid;
    return grid;
  }


  setYAxis(set: Series, index: number, seriesMinMax: any) {

    let axis = {
      id: set.name + index,
      scale: true,
      type: "value",
      show: true,
      min: roundDown(seriesMinMax.min, 10),
      max: roundUp(seriesMinMax.max, 10),
      nameTextStyle: {
        fontSize: (12 * this.zoomFactor),
      },
      position: "right",
      alignTicks: false,
      axisLabel: {
        fontSize: (12 * this.zoomFactor),
        formatter: function(value, index) {
          return value.toFixed(0);
        },
      },
      nameLocation: "end",
    };

    if (this.activity) {
      const activityTarget = set.activityTarget;
      if (activityTarget > seriesMinMax.max) {
        objAssign(axis, { max: activityTarget });
      }
    }

    let filteredData = this.runDataFilter();
    let lineMinMax = this.getSeriesMinMax(filteredData);

    if ([EgpChartType.COMBO, EgpChartType.MULTI_LINE, EgpChartType.LINE].includes(this.chartType) && seriesMinMax) {
      if (["line", "scatter"].includes(set.type)) {
        if (!checkProp(axis, "min")) {
          objAssign(axis, { min: roundDown(lineMinMax.min, 10) });
        }
        if (!checkProp(axis, "max")) {
          objAssign(axis, { max: roundUp(lineMinMax.max, 10) });
        }
        let otherCommonAxises = this.chartOption.yAxis.filter(set => ["line", "scatter"].includes(set.type));

        if (otherCommonAxises.length > 0) {
          set.yAxisIndex = otherCommonAxises[0].yAxisIndex;
        } else {
          set.yAxisIndex = index;
        }

        axis.position = "left";
        // axis.nameTextStyle.align = 'right';
        axis.show = !this.chartOption.yAxis.some(axis => axis.show === true);
        axis.scale = false;
        this.chartOption.yAxis.push(axis);
      } else if (["bar"].includes(set.type)) {

        let maxTarget = set.activityTarget;
        if (!checkProp(axis, "min")) {
          objAssign(axis, { min: 0 });
        }
        if (!checkProp(axis, "max")) {
          let max = maxTarget;
          if (lineMinMax.max < maxTarget) {
          }
          objAssign(axis, { max: maxTarget });
        }

        set.yAxisIndex = index;
        axis.show = false;
        axis.scale = false;
        axis.position = "left";
        // axis.nameTextStyle.align = 'right';
        this.chartOption.yAxis.push(axis);
      }
    } else if ([EgpChartType.BAR].includes(this.chartType) && seriesMinMax) {
      set.yAxisIndex = index;
      if (index > 0) return;
      if (index > 0) {
        axis.show = true;
      }
      if (!this.fullScreen) {
        axis.show = false;
      }
      axis.scale = false;
      axis.position = "left";
      // axis.nameTextStyle.align = 'right';
      this.chartOption.yAxis.push(axis);
    }
    return axis;
  }

  runDataFilter() {
    return this.data.filter((set) => {
      if (!["line", "scatter"].includes(set.type)) {
        return false;
      }
      return ![this.chartService.obsTypes.waist, this.chartService.obsTypes.height].includes(set.classificatorCode);

    });
  }

  getSetName(set: Series) {
    if (set.data && set.data.length > 0) {
      return set.data[0][2];
    } else if (set.activityTypeId) {
      return this.chartService.getLabel(set.activityTypeId);
    } else if (set.classificatorCode) {
      return this.chartService.getLabel(set.classificatorCode);
    }
  }

  /**
   * Get highest and lowest common value from all series
   */

  getSeriesMinMax(series: Series[]) {
    let minMax = {
      name: "",
      max: 0,
      min: Infinity,
    };
    for (let serie of series) {
      minMax.name = serie.name;
      let min = this.minMax(serie)[0];
      let max = this.minMax(serie)[1];
      if (minMax.min > min) {
        minMax.min = min;
      }
      if (minMax.max < max) {
        minMax.max = max;
      }
    }
    this.serviceData.seriesMinMax = minMax;
    return minMax;
  }

  /**
   * Returns [min, max]
   */
  minMax(series: Series) {
    return series.data.reduce((acc: any[], val: number) => {
      acc[0] = acc[0] === undefined || val[1] < acc[0] ? val[1] : acc[0];
      acc[1] = acc[1] === undefined || val[1] > acc[1] ? val[1] : acc[1];
      return acc;
    }, []);
  }


  setLegendNames(set: Series) {
    if (!this.fullScreen) return; // disabled on fullscreen
    // this.chartOption.legend.data.push(set.name);
    // return this.chartOption.legend;
  }

  /**
   * Sets circle dot style on condition
   */

  setDots(set: Series, index: number) {
    let appearance = {
      symbol: "emptyCircle",
      symbolSize: (6 * this.zoomFactor),
      animation: true,
      lineStyle: {
        width: (2 * this.zoomFactor),
      },
      itemStyle: {
        borderWidth: (2 * this.zoomFactor),
      },
      emphasis: {
        scale: true,
        itemStyle: {
          borderWidth: (2 * this.zoomFactor),
        },
      },

    };
    if (["line", "scatter"].includes(set.type)) {
      return Object.assign(set, appearance);
    } else {
      return set;
    }
  }

  /**
   * Creates horizontal markLine on chart for goal target when it's not range type
   */
  setSingleMarkLine(set: Series, text: string, value: number, unitValue?: string) {
    let mark = {
      yAxis: value,
      lineStyle: {
        type: "dashed",
        color: EgpChartColor.DARK_GRAY,
        width: (1 * this.zoomFactor),
      },
      label: {
        position: "insideMiddleBottom",
        color: EgpChartColor.DARK_GRAY,
        formatter: "",
        fontSize: (8 * this.zoomFactor),
      },
      emphasis: {
        label: {},
        lineStyle: {
          width: (1 * this.zoomFactor),
          color: EgpChartColor.DARK_GRAY,
          type: "solid",
        },
      },
    };

    mark.label.formatter = (text + value + " " + unitValue).toUpperCase();
    if (!set.markLine.hasOwnProperty("symbol")) {
      Object.assign(set.markLine, { symbol: ["none", "none"] });
    }

    set.markLine.data.push(mark);
    return mark;
  }


  setHealthPlanStartEndMarker(set: Series, seriesMinMax: any, index: number) {
    if (!this.useHealthPlanMarkLines) return;
    if (!this.healthPlan) return;
    if (index > 0) return;

    let x = {
      yAxis: seriesMinMax.min,
      xAxis: "",
    };
    let y = {
      yAxis: "max",
      xAxis: "",
      lineStyle: {
        type: "dashed",
        color: EgpChartColor.DARK_GRAY,
      },
      label: {
        position: "end",
        formatter: "",
        color: EgpChartColor.DARK_GRAY,
        fontSize: (8 * this.zoomFactor),
      },
      emphasis: {
        label: {},
        lineStyle: {
          color: EgpChartColor.DARK_GRAY,
          width: (2 * this.zoomFactor),
          type: "solid",
        },
      },
    };

    if (!set.markLine.hasOwnProperty("symbol")) {
      Object.assign(set.markLine, { symbol: ["none", "none"] });
    }

    if (this.healthPlan.periodStart) {
      if (this.chartType === EgpChartType.COMBO) {
        const startWeekDate = startOfISOWeek(new Date(this.healthPlan.periodStart));
        x.xAxis = getDayString(startWeekDate);
        y.xAxis = getDayString(startWeekDate);
        if (seriesMinMax.min === 1) {
          x.yAxis = 0;
        }
      } else {
        x.xAxis = this.healthPlan.periodStart.toISOString();
        y.xAxis = this.healthPlan.periodStart.toISOString();
      }

      y.label.formatter = this.translate.instant("healthPlan.healthPlanStart").toUpperCase();
      set.markLine.data.push([JSON.parse(JSON.stringify(x)), JSON.parse(JSON.stringify(y))]);
    }

    if (this.healthPlan.periodEnd) {
      if (this.chartType === EgpChartType.COMBO) {
        x.xAxis = getDayString(startOfISOWeek(new Date(this.healthPlan.periodEnd)));
        y.xAxis = getDayString(startOfISOWeek(new Date(this.healthPlan.periodEnd)));
        if (seriesMinMax.min === 1) {
          x.yAxis = 0;
        }
      } else {
        x.xAxis = this.healthPlan.periodEnd.toISOString();
        y.xAxis = this.healthPlan.periodEnd.toISOString();
      }
      y.label.formatter = this.translate.instant("healthPlan.healthPlanEnd").toUpperCase();
      set.markLine.data.push([JSON.parse(JSON.stringify(x)), JSON.parse(JSON.stringify(y))]);
    }

    return { x, y };
  }


  /**
   * Aggregate all dates from all series to a single array
   */
  getXAxisDates(sets: Series[]) {
    let arr: string[] = [];
    let earliestDate: Date = new Date();

    for (let set of sets) {
      let flatItems = set.data.flat(1);
      for (let item of flatItems) {
        if (this.isValidDate(item) && !arr.includes(item)) {
          arr.push(item);
          const itemDate = new Date(item);
          // set the earliest and latest dates of the measurements
          if (!this.healthPlan && itemDate.getTime() < earliestDate.getTime()) {
            earliestDate = itemDate;
          }
        }
      }
    }

    if (this.healthPlan) {
      earliestDate = this.healthPlan.periodStart;
    }

    if (!this.skipWeeks) {
      arr = sortItems(arr);

      if (isAfter(new Date(arr[0]), new Date(earliestDate))) {
        arr.unshift(getWeekStartString(earliestDate));
      }

      let weekToCheck = startOfISOWeek(new Date(arr[0]));
      const latestDate = startOfISOWeek(new Date(arr[arr.length - 1]));

      while (weekToCheck <= latestDate) {
        const weekNumber = getISOWeek(weekToCheck);
        const year = getYear(weekToCheck);

        const weekExists = arr.some(dateStr => {
          const date = new Date(dateStr);
          return getISOWeek(date) === weekNumber && getYear(date) === year;
        });

        if (!weekExists) {
          arr.push(getDayString(weekToCheck));
        }

        weekToCheck = addWeeks(weekToCheck, 1);
      }
    }
    arr = [...new Set(arr)];
    return arr;
  }

  /**
   * Validates whether the given input represents a valid date.
   * The input can be either a string or a Date object. Numbers and other types are not allowed.
   */
  isValidDate(d: any): boolean {
    if (typeof d === 'string' || d instanceof Date) {
      const date = new Date(d);
      return isValid(date) && !isNaN(date.getTime());
    }
    return false;
  }

  /**
   * Default options common to all builds
   */
  setOptions() {
    this.chartOption = {
      color: [
        EgpChartColor.GREEN,
        EgpChartColor.YELLOW,
        EgpChartColor.BLUE,
        EgpChartColor.MAIN,
        EgpChartColor.MAIN,
        EgpChartColor.MAIN,
        EgpChartColor.MAIN,
        EgpChartColor.MAIN,
        EgpChartColor.MAIN,
        EgpChartColor.MAIN,
      ],
      title: {
        text: "",
        subtext: "",
      },
      tooltip: null,
      grid: {
        left: "0%",
        right: "0%",
        bottom: 30,
      },
      toolbox: null,
      visualMap: [],
      legend: {
        data: [],
        selected: {},
      },
      dataZoom: null,
      xAxis: [],
      yAxis: [],
      series: [],
    };
  }

  getTableContent(opt: any): string {
    const axisData = opt.xAxis[0].data;
    const series = opt.series;

    let table = `
    <table class="table">
        <thead>
            <tr class="table-header-row">
                <th class="table-header-cell">Aeg</th>
                <th class="table-header-cell">${series[0]?.name || '-'}</th>
                <th class="table-header-cell">${series[1]?.name || '-'}</th>
                <th class="table-header-cell">${series[2]?.name || '-'}</th>
            </tr>
        </thead>
        <tbody>
`;

    for (let i = 0, l = axisData.length; i < l; i++) {
      const formattedDate = format(new Date(axisData[i]), "dd.MM.yyyy HH:mm");
      const height =
        series[0]?.data[i]?.[1] !== undefined && series[0]?.data[i]?.[2] !== undefined
          ? series[0].data[i][1] + series[0].data[i][2]
          : '-';

      const weight =
        series[1]?.data[i]?.[1] !== undefined && series[1]?.data[i]?.[2] !== undefined
          ? series[1].data[i][1] + series[1].data[i][2]
          : '-';

      const circumference =
        series[2]?.data[i]?.[1] !== undefined && series[2]?.data[i]?.[2] !== undefined
          ? series[2].data[i][1] + series[2].data[i][2]
          : '-';

      table += `
        <tr>
            <td class="table-cell">${formattedDate}</td>
            <td class="table-cell">${height}</td>
            <td class="table-cell">${weight}</td>
            <td class="table-cell">${circumference}</td>
        </tr>
    `;
    }

    table += `
            </tbody>
        </table>
    `;

    return table;
  }


  getLocaleLang(): string {
    let lang = localStorage.getItem("lang");
    if (lang) {
      if (lang.toUpperCase() == "ET") {
        lang = "EE";
      } else if (lang.toUpperCase() === "EN") {
        lang = "EN";
      } else if (lang.toUpperCase() === "RU") {
        lang = "RU";
      }


      return lang.toUpperCase();
    } else {
      return "EE"; // fallback
    }
  }

  onChartFinish(event: any) {
  }

  /**
   * Trigger on Zoom event. And update current zoom in options, so data refresh would keep zoom level.
   * On new data zoom is small % more out to fit new data
   */
  onZoom(event: any) {
    if (event.start && event.end) {
      this.currentZoom.end = event.end;
      this.currentZoom.start = event.start;
    } else if (event.batch) {
      this.currentZoom.end = event.batch[0].end;
      this.currentZoom.start = event.batch[0].start;
    }
    const result = this.chartOption.dataZoom.find(obj => {
      return obj.type === "inside";
    });
    if (result) {
      result.end = this.currentZoom.end;
      result.start = this.currentZoom.start;
    }
  }

  private getApplication() {
    this.application = (JSON.parse(sessionStorage.getItem("sessionContext")).application).toUpperCase();
    return this.application;
  }
}

export function objAssign(obj, prop) {
  Object.assign(obj, prop);
}

export function checkProp(obj, prop) {
  return Object(obj).hasOwnProperty(prop);
}

export function roundUp(num: number, roundPlaces: number) {
  return Math.ceil(num / roundPlaces) * roundPlaces;
}

export function roundDown(num: number, roundPlaces: number) {
  return Math.floor(num / roundPlaces) * roundPlaces;
}
